import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { COUNTRIES_JSON } from '../../utils/locations';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { SelectChangeEvent } from '@mui/material';

type Country = { name: string; id: number };

export default function CountrySelect({
  register,
  registerValue,
  setValue,
  handleCountyCodeChange,
  originalCountry,
  id,
}: {
  register: UseFormRegister<any>;
  registerValue: string;
  setValue: UseFormSetValue<any>;
  originalCountry?: string;
  id: string;
  handleCountyCodeChange: (code: number) => void;
}) {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [value, setValueLocal] = useState<string>('');

  useEffect(() => {
    setLoading(true);
    fetch(COUNTRIES_JSON)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load (${res.status})`);
        return res.json() as Promise<Country[]>;
      })
      .then((data) => {
        setCountries(data);
        if (originalCountry) {
          const found = data.find((c) => c.name === originalCountry);
          if (found) {
            setValueLocal(found.name);
            setValue(registerValue, found.name);
            handleCountyCodeChange(found.id);
          } else {
            setValue(registerValue, originalCountry);
          }
        } else {
          // Initialize to the United States if no original country is provided
          const defaultCountry = data.find((c) => c.name === 'United States');
          if (defaultCountry) {
            setValueLocal(defaultCountry.name);
            setValue(registerValue, defaultCountry.name);
            handleCountyCodeChange(defaultCountry.id);
          }
        }
      })
      .catch((err) => {
        console.error('Error loading countries:', err);
        setCountries([]);
      })
      .finally(() => setLoading(false));
  }, [setValue, handleCountyCodeChange]);

  const handleChange = useCallback((event: SelectChangeEvent<string>) => {
    const name = event.target.value as string;
    setValueLocal(name);
    setValue(registerValue, name);

    const found = countries.find((c) => c.name === name);
    if (found) {
      handleCountyCodeChange(found.id);
    }
  }, [countries, registerValue, setValue, handleCountyCodeChange]);

  return (
    <Grid item xs={12} md={6}>
      <FormControl fullWidth margin="normal" required disabled={loading}>
        <InputLabel id="country-select-label">Country</InputLabel>
        <Select
          labelId="country-select-label"
          id={id}
          value={value}
          label="Country"
          {...register(registerValue)}
          onChange={handleChange}
        >
          {loading ? (
            <MenuItem value="">
              <CircularProgress size={20} /> Loading…
            </MenuItem>
          ) : (
            countries.map(({ name }) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))
          )}
        </Select>
        {loading && <FormHelperText>Fetching country list…</FormHelperText>}
      </FormControl>
    </Grid>
  );
}
